import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useToast } from 'components/hooks/useToast';
import { useHistory } from 'react-router-dom';
import { useTalentPlanningSidepanelData } from 'reducers/talentPlanningSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { useBulkActionContext } from '../BulkActions/BulkActionContext';
import { REVIEW_STATUS_VALUES, usePartialPublishContext } from './PartialPublishContext';

export type SectionId = 'ALLOW_MANAGER_PUBLISH' | 'ALLOW_EMPLOYEE_PUBLISH' | 'NO_ACTION';
export type ReviewStatus = keyof typeof REVIEW_STATUS_VALUES;

export interface EmployeeWithReviewStatus {
  employeeDetails: {
    id: number;
    displayName: string;
    profilePhoto: string;
    designation: string;
    ownerType: string;
    employeeFunction: string;
    email: string;
    preferredName: string;
  };
  reviewStatus: ReviewStatus;
}

export interface Section {
  section: SectionId;
  employeeStatusList: EmployeeWithReviewStatus[];
  competencyScorePushEmpCount: number | null;
}

interface PublishOptionType {
  totalEmployeeCount: number;
  sections: Section[];
}

const getPartialPublishOptionsApi = ({
  employeesList,
  reviewCycleId,
  cohortId,
}): Promise<{ entity: PublishOptionType }> => {
  const url = endpoints.analytics.review.partialPublish.options;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      employeesList,
      reviewCycleId,
      cohortId,
    },
    queryParams: {},
  });
};

const submitPartialPublishApi = ({
  employeesList,
  reviewCycleId,
  cohortId,
  publishStatus,
}: {
  publishStatus: string;
  employeesList: number[];
  reviewCycleId: number;
  cohortId: number;
}) => {
  const url = endpoints.analytics.review.partialPublish.publish;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      publishStatus,
      employeesList,
      reviewCycleId,
      cohortId,
    },
    queryParams: {},
  });
};

export const useTargetReviewCycleId = () => {
  const history = useHistory();
  const { reviewCycleId: reviewManageReviewCycleId } = useQueryParams();
  const { selectedCalibrationView } = useTalentPlanningSidepanelData();
  const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);
  const reviewCycleId = isTalentPlanningPage ? selectedCalibrationView?.targetCycleId : reviewManageReviewCycleId;
  return reviewCycleId;
};

export const useGetPartialPublishOptionsQuery = () => {
  const { selectedEmployeeIds } = useBulkActionContext();
  const { id: cohortId } = useSelectedView();

  const reviewCycleId = useTargetReviewCycleId();
  const toast = useToast();

  return useQuery(
    [REACT_QUERY_KEYS.partialPublish, ...selectedEmployeeIds, reviewCycleId, cohortId],
    () =>
      getPartialPublishOptionsApi({
        employeesList: selectedEmployeeIds,
        reviewCycleId: +reviewCycleId,
        cohortId: +cohortId,
      }).then((res) => res.entity),
    {
      enabled: selectedEmployeeIds.length > 0 && !!reviewCycleId && !!cohortId,
      onError: (error) => {
        toast({
          title: (error as Error)?.message || 'Failed to publish. Please try again.',
          status: 'error',
        });
      },
    }
  );
};

export const useSubmitPartialPublishMutation = () => {
  const { selectedSection, setPublishSuccessful } = usePartialPublishContext();
  const reviewCycleId = useTargetReviewCycleId();
  const queryClient = useQueryClient();
  const { id: cohortId } = useSelectedView();

  return useMutation(
    () =>
      submitPartialPublishApi({
        employeesList: selectedSection.employeeStatusList.map((e) => e.employeeDetails.id),
        reviewCycleId: +reviewCycleId,
        cohortId: +cohortId,
        publishStatus: selectedSection?.section === 'ALLOW_MANAGER_PUBLISH' ? 'PUBLISHED_TO_MANAGER' : 'PUBLISHED',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewsManage]);
        queryClient.invalidateQueries([REACT_QUERY_KEYS.talentPlanning.tabularData]);
        setPublishSuccessful(true);
      },
      onError: () => {
        setPublishSuccessful(false);
      },
      mutationKey: [REACT_QUERY_KEYS.partialPublish],
    }
  );
};

export const useIsPublishingInProgress = () => useIsMutating({ mutationKey: [REACT_QUERY_KEYS.partialPublish] }) > 0;
